'use client';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { authApi } from '../../../lib/api';
import { useAuthStore, useIsHydrated } from '../../../store/auth-store';
import { useToast } from '../../../hooks/use-toast';
import { Button } from '../../../components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Toaster } from '../../../components/ui/toaster';
import { PasswordInput } from '../../../components/ui/password-input';

const formSchema = z.object({
  username: z.string().min(3, {
    message: 'Username must be at least 3 characters.',
  }),
  password: z.string().min(6, {
    message: 'Password must be at least 6 characters.',
  }),
});

export default function LoginPage() {
  const router = useRouter();
  const { toast } = useToast();
  const { setAuth, isAuthenticated, hasEmailConfig } = useAuthStore();
  const isHydrated = useIsHydrated();
  const [isLoading, setIsLoading] = useState(true);

  // Redirect if already authenticated
  useEffect(() => {
    if (!isHydrated) {
      return; // Wait for hydration before redirecting
    }

    if (isAuthenticated) {
      if (!hasEmailConfig) {
        router.push('/email-config');
      } else {
        router.push('/dashboard');
      }
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, hasEmailConfig, router, isHydrated]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const { handleSubmit, control, formState } = form;
  const { isSubmitting, errors } = formState;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const response = await authApi.login({
        username: values.username,
        password: values.password,
      });

      if (response.data.success) {
        toast({
          title: 'Login successful',
          description: 'Welcome back to AutoDM!',
          variant: 'success',
        });

        setAuth(response.data.data);

        if (!response.data.data.hasEmailConfig) {
          router.push('/email-config');
        } else {
          router.push('/dashboard');
        }
      }
    } catch (error: any) {
      console.error('Login failed:', error);
      toast({
        title: 'Login failed',
        description:
          error.response?.data?.error || 'Invalid username or password',
        variant: 'destructive',
      });
    }
  };

  // Don't render anything until we're sure about authentication state
  if (isLoading && isHydrated) {
    return null;
  }

  return (
    <div className="flex justify-center items-center min-h-screen bg-background">
      <Card className="w-[350px]">
        <CardHeader>
          <CardTitle>Log in</CardTitle>
          <CardDescription>
            Enter your username and password to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input placeholder="username" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <PasswordInput placeholder="••••••" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Logging in...' : 'Log in'}
              </Button>
            </form>
          </Form>
          <div className="mt-4 text-center">
            <Link
              href="/forgot-password"
              className="text-sm text-primary hover:underline"
            >
              Forgot password?
            </Link>
          </div>
        </CardContent>
      </Card>
      <Toaster />
    </div>
  );
}

//  <CardFooter className="flex justify-center">
//           <p className="text-sm text-muted-foreground">
//             Don&apos;t have an account?{' '}
//             <Link href="/register" className="text-primary hover:underline">
//               Register
//             </Link>
//           </p>
//         </CardFooter>
